import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TermsPage = () => {
  const [policies, setPolicies] = useState({
    returns: `We want you to be satisfied with your purchase. Our return policy includes:
• Returns accepted within 30 days of delivery for unopened products
• Products must be in original packaging and resalable condition
• Opened products may only be returned if there is a documented quality issue
• Refunds will be processed to the original payment method within 5-7 business days
• Shipping costs for returns are the customer's responsibility unless the return is due to our error`,
    contact_email: "hello@livelifeglobal.com",
    contact_phone: "1-800-WELLNESS"
  });

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        const data = response.data;
        
        if (data?.policies?.returns) {
          setPolicies(prev => ({
            ...prev,
            returns: data.policies.returns
          }));
        }
        if (data?.footer?.contact_email) {
          setPolicies(prev => ({
            ...prev,
            contact_email: data.footer.contact_email
          }));
        }
        if (data?.footer?.contact_phone) {
          setPolicies(prev => ({
            ...prev,
            contact_phone: data.footer.contact_phone
          }));
        }
      } catch (error) {
        // Use default policies if fetch fails
      }
    };
    fetchPolicies();
  }, []);

  // Parse returns policy text into lines for rendering
  const renderReturnsPolicy = () => {
    // Simply render the policy as pre-formatted text with whitespace preserved
    return (
      <div className="text-text-secondary leading-relaxed whitespace-pre-line">
        {policies.returns}
      </div>
    );
  };

  return (
    <div className="animate-fadeIn" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <h1 className="font-serif text-4xl md:text-5xl text-text-primary mb-8">
          Terms of Use
        </h1>
        
        <div className="prose prose-sage max-w-none space-y-8">
          <p className="text-text-secondary text-lg leading-relaxed">
            Last Updated: January 2026
          </p>

          <section>
            <h2 className="font-serif text-2xl text-text-primary mt-8 mb-4">
              1. Educational Purpose Only
            </h2>
            <p className="text-text-secondary leading-relaxed">
              The information provided on this website is for educational and informational purposes only. 
              Live Life Global Wellness does not provide medical advice, diagnosis, or treatment. 
              The content on this site is not intended to be a substitute for professional medical advice, 
              diagnosis, or treatment. Always seek the advice of your physician or other qualified health 
              provider with any questions you may have regarding a medical condition.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-text-primary mt-8 mb-4">
              2. No Medical Claims
            </h2>
            <p className="text-text-secondary leading-relaxed">
              Our products are not intended to diagnose, treat, cure, or prevent any disease. 
              The statements made on this website have not been evaluated by the Food and Drug Administration (FDA). 
              Individual results may vary. If you are pregnant, nursing, taking medication, or have a 
              medical condition, consult your physician before using any of our products.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-text-primary mt-8 mb-4">
              3. Customer Responsibility
            </h2>
            <p className="text-text-secondary leading-relaxed">
              By purchasing and using our products, you acknowledge that:
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-text-secondary">
              <li>You have read and understood the product descriptions and usage instructions</li>
              <li>You are responsible for determining if a product is appropriate for your individual needs</li>
              <li>You will consult with a healthcare professional before starting any supplement regimen</li>
              <li>You will follow all product storage and usage guidelines</li>
              <li>You will discontinue use and seek medical attention if you experience any adverse reactions</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-text-primary mt-8 mb-4">
              4. Limitation of Liability
            </h2>
            <p className="text-text-secondary leading-relaxed">
              To the fullest extent permitted by applicable law, Live Life Global Wellness and its officers, 
              directors, employees, and agents shall not be liable for any indirect, incidental, special, 
              consequential, or punitive damages arising out of or relating to your use of our products or services.
            </p>
            <p className="text-text-secondary leading-relaxed mt-4">
              Our total liability for any claims under these terms shall be limited to the amount you paid 
              for the product(s) giving rise to the claim. This limitation applies regardless of the 
              form of action, whether in contract, tort, strict liability, or otherwise.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-text-primary mt-8 mb-4">
              5. Returns and Refunds
            </h2>
            {renderReturnsPolicy()}
          </section>

          <section className="bg-cream-dark rounded-2xl p-6 md:p-8 mt-12">
            <h2 className="font-serif text-2xl text-text-primary mb-4">
              6. Third-Party Services – Wellness Trips
            </h2>
            <p className="text-text-secondary leading-relaxed">
              <strong>Important Notice:</strong> Wellness Trips featured on our website are provided by 
              Circulate Travel, an independent third-party travel service provider. Live Life Global Wellness 
              is not affiliated with, nor responsible for, the services provided by Circulate Travel.
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-text-secondary">
              <li>
                <strong>Booking and Payments:</strong> All travel bookings, payments, and related transactions 
                are processed directly by Circulate Travel. Live Life Global Wellness does not process, 
                hold, or manage any travel-related payments.
              </li>
              <li>
                <strong>Liability:</strong> Live Life Global Wellness is not responsible for any bookings, 
                cancellations, refunds, itinerary changes, accommodations, transportation, or any other 
                services provided by Circulate Travel.
              </li>
              <li>
                <strong>Disputes:</strong> Any disputes, claims, or issues arising from Wellness Trips 
                must be directed to Circulate Travel. Live Life Global Wellness will not mediate or 
                be involved in resolving such matters.
              </li>
              <li>
                <strong>Terms:</strong> When you book a Wellness Trip, you agree to Circulate Travel's 
                terms of service, privacy policy, and cancellation policies. We encourage you to review 
                their policies before making any booking.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-text-primary mt-8 mb-4">
              7. Intellectual Property
            </h2>
            <p className="text-text-secondary leading-relaxed">
              All content on this website, including text, graphics, logos, images, and software, 
              is the property of Live Life Global Wellness or its content suppliers and is protected 
              by United States and international copyright laws.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-text-primary mt-8 mb-4">
              8. Governing Law
            </h2>
            <p className="text-text-secondary leading-relaxed">
              These Terms of Use shall be governed by and construed in accordance with the laws 
              of the State of California, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-text-primary mt-8 mb-4">
              9. Contact Information
            </h2>
            <p className="text-text-secondary leading-relaxed">
              If you have any questions about these Terms of Use, please contact us at:
            </p>
            <p className="text-text-secondary mt-4">
              <strong>Email:</strong> {policies.contact_email}<br />
              {policies.contact_phone && <><strong>Phone:</strong> {policies.contact_phone}</>}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
